import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Loader2,
  AlertCircle,
  X,
  Calendar,
  Eye,
  EyeOff,
  Save,
  User,
  CheckCircle,
  XCircle,
  TrendingUp,
} from "lucide-react";
import { adminService } from "../../api/services/adminService";
import { useToast } from "../../components/common/ThemeToaster";

const AdminsManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    superAdmins: 0,
    admins: 0,
    editors: 0,
    active: 0,
    inactive: 0,
    recentlyActive: 0,
    withProfilePictures: 0,
  });
  const [selectedAdmins, setSelectedAdmins] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const { success: toastSuccess, error: toastError } = useToast();

  // Fetch admins on component mount
  useEffect(() => {
    fetchAdmins();
    fetchStats();
  }, [currentPage, roleFilter, statusFilter, searchTerm]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: 20,
        ...(roleFilter !== "all" && { role: roleFilter }),
        ...(statusFilter !== "all" && { isActive: statusFilter === "active" }),
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await adminService.getAllAdmins(params);

      let adminsData = [];
      let paginationData = {};

      if (response?.data?.data?.admins) {
        adminsData = response.data.data.admins;
        paginationData = response.data.data.pagination;
      } else if (response?.data?.admins) {
        adminsData = response.data.admins;
        paginationData = response.data.pagination;
      } else if (response?.admins) {
        adminsData = response.admins;
        paginationData = response.pagination;
      } else if (Array.isArray(response?.data?.data)) {
        adminsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        adminsData = response.data;
      } else if (Array.isArray(response)) {
        adminsData = response;
      }

      setAdmins(adminsData);
      setTotalAdmins(paginationData?.total || adminsData.length);
      setTotalPages(paginationData?.pages || paginationData?.totalPages || 1);
    } catch (err) {
      console.error("Error fetching admins:", err);
      toastError(err.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminService.getAdminStats();

      let statsData = {};
      if (response?.data?.data) {
        statsData = response.data.data;
      } else if (response?.data) {
        statsData = response.data;
      } else if (response?.stats) {
        statsData = response.stats;
      }

      setStats({
        total: statsData.total || 0,
        superAdmins: statsData.byRole?.super_admin || 0,
        admins: statsData.byRole?.admin || 0,
        editors: statsData.byRole?.editor || 0,
        active: statsData.active || 0,
        inactive: statsData.inactive || 0,
        recentlyActive: statsData.recentlyActive || 0,
        withProfilePictures: statsData.withProfilePictures || 0,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      setActionLoading(true);
      await adminService.toggleAdminStatus(admin.id, !admin.isActive);
      toastSuccess(
        `Admin ${!admin.isActive ? "activated" : "deactivated"} successfully`,
      );
      await fetchAdmins();
      await fetchStats();
    } catch (err) {
      toastError(err.message || "Failed to update admin status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async (adminId, newRole) => {
    try {
      setActionLoading(true);
      await adminService.updateAdminRole(adminId, newRole);
      toastSuccess("Admin role updated successfully");
      await fetchAdmins();
      setShowRoleModal(false);
      setSelectedAdmin(null);
    } catch (err) {
      toastError(err.message || "Failed to update admin role");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      setActionLoading(true);
      await adminService.deleteAdmin(selectedAdmin.id);
      toastSuccess("Admin deleted successfully");
      await fetchAdmins();
      await fetchStats();
      setShowDeleteModal(false);
      setSelectedAdmin(null);
    } catch (err) {
      toastError(err.message || "Failed to delete admin");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedAdmins.size) return;
    if (!window.confirm(`Delete ${selectedAdmins.size} admin(s)?`)) return;

    try {
      const adminIds = Array.from(selectedAdmins);
      await adminService.bulkDeleteAdmins(adminIds);
      toastSuccess(`${adminIds.length} admin(s) deleted successfully`);
      setSelectedAdmins(new Set());
      setSelectAll(false);
      await fetchAdmins();
      await fetchStats();
    } catch (err) {
      toastError(err.message || "Failed to delete admins");
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedAdmins.size) return;

    try {
      const adminIds = Array.from(selectedAdmins);
      await adminService.bulkUpdateStatus(adminIds, isActive);
      toastSuccess(
        `${adminIds.length} admin(s) ${isActive ? "activated" : "deactivated"} successfully`,
      );
      setSelectedAdmins(new Set());
      setSelectAll(false);
      await fetchAdmins();
      await fetchStats();
    } catch (err) {
      toastError(err.message || "Failed to update admin status");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedAdmins(selectAll ? new Set() : new Set(admins.map((a) => a.id)));
  };

  const handleSelectAdmin = (id) => {
    const newSet = new Set(selectedAdmins);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedAdmins(newSet);
    setSelectAll(newSet.size === admins.length);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "super_admin":
        return <ShieldAlert size={14} className="text-purple-500" />;
      case "admin":
        return <ShieldCheck size={14} className="text-blue-500" />;
      default:
        return <Shield size={14} className="text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin":
        return "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border-purple-200 dark:border-purple-800";
      case "admin":
        return "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200 dark:border-blue-800";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700";
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return {
        color:
          "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800",
        icon: CheckCircle,
        label: "Active",
      };
    }
    return {
      color:
        "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800",
      icon: XCircle,
      label: "Inactive",
    };
  };

  const StatCard = ({ icon: Icon, label, value, trend }) => (
    <motion.div
      whileHover={{ y: -2, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
      <div className="relative p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md">
            <Icon size={20} className="text-white" />
          </div>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {value?.toLocaleString() || 0}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {label}
        </p>
        {trend && (
          <div className="flex items-center gap-1 mt-2 text-xs">
            <TrendingUp size={12} className="text-emerald-500" />
            <span className="text-emerald-600 dark:text-emerald-400">
              +{trend}%
            </span>
            <span className="text-gray-500">vs last week</span>
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500" />
    </motion.div>
  );

  const statCards = [
    { icon: User, label: "Total Admins", value: stats.total, trend: 5 },
    { icon: ShieldAlert, label: "Super Admins", value: stats.superAdmins },
    { icon: ShieldCheck, label: "Admins", value: stats.admins },
    { icon: Shield, label: "Editors", value: stats.editors },
    { icon: CheckCircle, label: "Active", value: stats.active },
  ];

  if (loading && admins.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading admins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Administrators
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage system administrators and their permissions
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
          >
            <UserPlus size={18} />
            Add New Admin
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard
              key={i}
              icon={card.icon}
              label={card.label}
              value={card.value}
              trend={card.trend}
            />
          ))}
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              onClick={fetchAdmins}
              className="px-4 py-2.5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border rounded-xl flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />{" "}
              Refresh
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedAdmins.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedAdmins.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkStatus(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkStatus(false)}
                className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                Deactivate
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Admins Table */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-5 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Admin
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Email
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Role
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Last Login
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                <AnimatePresence>
                  {admins.map((admin) => {
                    const statusBadge = getStatusBadge(admin.isActive);
                    const StatusIcon = statusBadge.icon;

                    return (
                      <motion.tr
                        key={admin.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                        className="group border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="px-5 py-4">
                          <input
                            type="checkbox"
                            checked={selectedAdmins.has(admin.id)}
                            onChange={() => handleSelectAdmin(admin.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white text-sm font-medium shadow-sm">
                              {admin.firstName?.charAt(0)}
                              {admin.lastName?.charAt(0)}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {admin.firstName} {admin.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {admin.email}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {getRoleIcon(admin.role)}
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(admin.role)} border`}
                            >
                              {admin.role?.replace("_", " ").toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge.color} border`}
                          >
                            <StatusIcon size={10} />
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {admin.lastLoginAt
                            ? new Date(admin.lastLoginAt).toLocaleDateString()
                            : "Never"}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowEditModal(true);
                              }}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              title="Edit"
                            >
                              <Edit2 size={14} className="text-blue-500" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowRoleModal(true);
                              }}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              title="Change Role"
                            >
                              <Shield size={14} className="text-purple-500" />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(admin)}
                              disabled={actionLoading}
                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              title={admin.isActive ? "Deactivate" : "Activate"}
                            >
                              {admin.isActive ? (
                                <ToggleRight
                                  size={14}
                                  className="text-green-500"
                                />
                              ) : (
                                <ToggleLeft
                                  size={14}
                                  className="text-gray-500"
                                />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {admins.length === 0 && !loading && (
            <div className="text-center py-12">
              <User size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No admins found</h3>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search"
                  : "Click 'Add New Admin' to create one"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-1.5">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedAdmin && (
          <EditAdminModal
            admin={selectedAdmin}
            onClose={() => {
              setShowEditModal(false);
              setSelectedAdmin(null);
            }}
            onSuccess={() => {
              fetchAdmins();
              setShowEditModal(false);
              setSelectedAdmin(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Change Role Modal */}
      <AnimatePresence>
        {showRoleModal && selectedAdmin && (
          <ChangeRoleModal
            admin={selectedAdmin}
            onClose={() => {
              setShowRoleModal(false);
              setSelectedAdmin(null);
            }}
            onConfirm={handleUpdateRole}
            loading={actionLoading}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedAdmin && (
          <DeleteConfirmationModal
            admin={selectedAdmin}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedAdmin(null);
            }}
            onConfirm={handleDeleteAdmin}
            loading={actionLoading}
          />
        )}
      </AnimatePresence>

      {/* Add Admin Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddAdminModal
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              fetchAdmins();
              fetchStats();
              setShowAddModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Edit Admin Modal Component
const EditAdminModal = ({ admin, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: admin.firstName || "",
    lastName: admin.lastName || "",
    phoneNumber: admin.phoneNumber || "",
    profilePictureAlt: admin.profilePictureAlt || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { success: toastSuccess, error: toastError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      await adminService.updateProfile(formData);
      toastSuccess("Admin updated successfully");
      onSuccess();
    } catch (err) {
      setError(err.message || "Failed to update admin");
      toastError(err.message || "Failed to update admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Edit Admin
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 rounded-xl flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              placeholder="+1234567890"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profile Picture Alt Text
            </label>
            <input
              type="text"
              value={formData.profilePictureAlt}
              onChange={(e) =>
                setFormData({ ...formData, profilePictureAlt: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              placeholder="Profile picture description for accessibility"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Change Role Modal
const ChangeRoleModal = ({ admin, onClose, onConfirm, loading }) => {
  const [selectedRole, setSelectedRole] = useState(admin.role);

  const roles = [
    {
      value: "super_admin",
      label: "Super Admin",
      description: "Full system access with all permissions",
    },
    {
      value: "admin",
      label: "Admin",
      description: "Can manage most resources except super admin settings",
    },
    {
      value: "editor",
      label: "Editor",
      description: "Can create and edit content only",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Change Role
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Update role for{" "}
          <span className="font-medium text-gray-900 dark:text-white">
            {admin.firstName} {admin.lastName}
          </span>
        </p>

        <div className="space-y-3 mb-6">
          {roles.map((role) => (
            <label
              key={role.value}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                selectedRole === role.value
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="role"
                value={role.value}
                checked={selectedRole === role.value}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="mt-1"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {role.label}
                </p>
                <p className="text-xs text-gray-500">{role.description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(admin.id, selectedRole)}
            disabled={loading || selectedRole === admin.role}
            className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              "Update Role"
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal = ({ admin, onClose, onConfirm, loading }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Trash2 size={32} className="text-red-500" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Delete Admin
        </h2>

        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Are you sure you want to delete{" "}
          <span className="font-medium text-gray-900 dark:text-white">
            {admin.firstName} {admin.lastName}
          </span>
          ? This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// Add Admin Modal
const AddAdminModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "admin",
    phoneNumber: "",
    profilePictureAlt: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      toastError("Passwords don't match");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      toastError("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const { confirmPassword, ...registrationData } = formData;
      await adminService.registerAdmin(registrationData);
      toastSuccess("Admin created successfully");
      onSuccess();
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to create admin");
      toastError(err.message || "Failed to create admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Add New Admin
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 rounded-xl flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className="w-full px-4 py-2.5 pr-10 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({ ...formData, confirmPassword: e.target.value })
                }
                className="w-full px-4 py-2.5 pr-10 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
              <option value="editor">Editor</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              placeholder="+1234567890"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profile Picture Alt Text
            </label>
            <input
              type="text"
              value={formData.profilePictureAlt}
              onChange={(e) =>
                setFormData({ ...formData, profilePictureAlt: e.target.value })
              }
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:ring-2 focus:ring-primary-500"
              placeholder="Profile picture description for accessibility"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <UserPlus size={18} />
              )}
              {loading ? "Creating..." : "Create Admin"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AdminsManagement;
